import { useEffect, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Icon,
  ImageCropper,
  Slider,
  toastManager,
  type ImageCropperHandle,
} from "@salestrak/ui";

const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.01;

interface UpdateProfilePhotoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Object URL of the picked image file. */
  imageUri: string;
  /** Receives the cropped image; resolve true to close the dialog. */
  onSave: (image: Blob) => Promise<boolean>;
}

export function UpdateProfilePhotoDialog({
  open,
  onOpenChange,
  imageUri,
  onSave,
}: UpdateProfilePhotoDialogProps) {
  const cropperRef = useRef<ImageCropperHandle>(null);
  const [zoom, setZoom] = useState(ZOOM_MIN);
  const [saving, setSaving] = useState(false);

  // Each newly-picked image starts at fit-to-frame.
  useEffect(() => {
    setZoom(ZOOM_MIN);
  }, [imageUri]);

  async function handleSave() {
    if (!cropperRef.current) return;
    setSaving(true);
    try {
      const image = await cropperRef.current.getCroppedBlob();
      const saved = await onSave(image);
      if (saved) onOpenChange(false);
    } catch {
      // onSave owns its own mutation error toasts; this catch only covers a
      // crop/encode failure (canvas.toBlob, decode) — show it instead of
      // silently stopping the spinner and leaving the dialog open.
      toastManager.add({ title: "Failed to process photo", type: "error" });
    } finally {
      setSaving(false);
    }
  }

  // One Dialog; a sheet on a phone (the responsive Dialog).
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup
        dialogOptions={{
          primary: { label: "Save photo", onClick: handleSave, disabled: saving, loading: saving },
          cancel: { disabled: saving },
        }}
      >
        <DialogHeader>
          <DialogTitle>Update profile photo</DialogTitle>
        </DialogHeader>
        <DialogPanel>
          <Box align="center" direction="column" gap="lg">
            <ImageCropper
              alt="New profile photo preview"
              ref={cropperRef}
              size={280}
              source={imageUri}
              zoom={zoom}
            />
            <Box align="center" direction="row" fill="width" gap="sm">
              <Icon name="zoom-out" size="sm" />
              <Box flex={1}>
                <Slider
                  aria-label="Zoom photo"
                  max={ZOOM_MAX}
                  min={ZOOM_MIN}
                  onValueChange={(next) =>
                    setZoom(Array.isArray(next) ? next[0] : next)
                  }
                  step={ZOOM_STEP}
                  value={zoom}
                />
              </Box>
              <Icon name="zoom-in" size="sm" />
            </Box>
          </Box>
        </DialogPanel>
      </DialogPopup>
    </Dialog>
  );
}
